import React from "react";
import { Pencil, Trash2, Plus, LogIn, UserCog } from "lucide-react";
import { toast } from "sonner";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { fetchAllPages } from "@/lib/serverExport";
import { http } from "@/lib/api";
import { useAuth, hasPerm } from "@/context/AuthContext";
import { useResourceMutations } from "@/hooks/useResource";
import { useOptions } from "@/hooks/useOptions";
import { PageHeader } from "@/components/kit/PageHeader";
import { DataTablePro } from "@/components/kit/DataTablePro";
import { StatusBadge } from "@/components/kit/StatusBadge";
import { useBulkActions } from "@/components/kit/bulk";
import { Button } from "@/components/ui/button";
import { fmtMoney, fmtDate, fmtDateTime, pubId, initials } from "@/lib/format";
import { isGlobal } from "@/lib/access";
import { useTenants } from "@/hooks/useTenants";
import { UserDrawer } from "@/drawers/UserDrawer";
import { SearchSelect } from "@/components/kit/SearchSelect";

const ROLE_LABELS = { super_admin: "Super Admin", tenant_admin: "Tenant Admin", manager: "Manager", buyer: "Buyer" };

export default function Users() {
  const { user, impersonateUser } = useAuth();
  const opts = useOptions();
  const [drawer, setDrawer] = React.useState({ open: false, item: null });
  // ---- Server-side pagination: DataTablePro reports page/size/applied filters; we fetch one page.
  const [srv, setSrv] = React.useState({ page: 1, size: 10, q: "", filters: {}, custom: {}, sort: null, dir: "asc" });
  const handleServerChange = React.useCallback((s) => setSrv(s), []);
  const qs = React.useMemo(() => {
    const p = new URLSearchParams();
    p.set("limit", String(srv.size));
    p.set("offset", String((srv.page - 1) * srv.size));
    if (srv.q.trim()) p.set("search", srv.q.trim());
    const f = srv.filters || {};
    ["role", "manager_id", "status", "tenant_id"].forEach((k) => {
      const v = f[k];
      if (v && v !== "all") p.set(k, v);
    });
    if (srv.sort) { p.set("sort", srv.sort); p.set("order", srv.dir || "asc"); }
    return p.toString();
  }, [srv]);
  const { data, isLoading } = useQuery({
    queryKey: ["users", "server", qs],
    queryFn: async () => (await http.get(`/users?${qs}`)).data,
    placeholderData: (prev) => prev,
  });
  const { remove } = useResourceMutations("users");
  const bulk = useBulkActions("users", { noun: "user" });
  const qc = useQueryClient();
  const [bulkMgr, setBulkMgr] = React.useState("");
  // Responsible users include tenant admins — a buyer managed by the Tenant Admin shows their name.
  const responsibles = opts.responsibles?.length ? opts.responsibles : opts.managers;
  const mgrName = (id) => (id && (responsibles.find((m) => m.id === id)?.name)) || "—";
  const showTenant = isGlobal(user, "users", "view");
  const showMoney = hasPerm(user, "payouts.view");
  const { data: tenantsData } = useTenants();
  const tenantName = (id) => (tenantsData?.items || []).find((t) => t.internalId === id)?.name || (id || "—");

  const columns = [
    { key: "id", label: "ID", mono: true, render: (r) => <span className="text-muted-foreground">{pubId(r)}</span> },
    { key: "name", label: "Name", sortable: true, render: (r) => (
      <div className="flex items-center gap-2.5">
        <div className="flex h-7 w-7 items-center justify-center overflow-hidden rounded-full bg-white/[0.08] text-[10px] text-muted-foreground">
          {r.avatar ? <img src={r.avatar} alt="" className="h-full w-full object-cover" /> : initials(r.name)}
        </div>
        <span className="font-medium text-foreground">{r.name}</span>
      </div>
    ) },
    ...(showTenant ? [{ key: "tenant", label: "Tenant", exportValue: (r) => tenantName(r.tenant_id), render: (r) => <span className="text-muted-foreground">{tenantName(r.tenant_id)}</span> }] : []),
    { key: "email", label: "Email", sortable: true, render: (r) => <span className="text-muted-foreground">{r.email}</span> },
    { key: "role", label: "Role", sortable: true, render: (r) => ROLE_LABELS[r.role] || r.role },
    { key: "manager", label: "Manager", exportValue: (r) => mgrName(r.manager_id), render: (r) => mgrName(r.manager_id) },
    { key: "status", label: "Status", sortable: true, render: (r) => <StatusBadge status={r.status} /> },
    ...(showMoney ? [
      { key: "available_balance", label: "Available Balance", align: "right", mono: true,
        exportValue: (r) => r.available_balance,
        render: (r) => r.role === "buyer" ? fmtMoney(r.available_balance) : <span className="text-muted-foreground/40">—</span> },
      { key: "hold_balance", label: "Hold Balance", align: "right", mono: true,
        exportValue: (r) => r.hold_balance,
        render: (r) => r.role === "buyer" ? fmtMoney(r.hold_balance) : <span className="text-muted-foreground/40">—</span> },
    ] : []),
    { key: "created_at", label: "Created", sortable: true, render: (r) => fmtDate(r.created_at) },
    { key: "last_login", label: "Last Login", sortable: true, render: (r) => r.last_login ? fmtDateTime(r.last_login) : <span className="text-muted-foreground/50">Never</span> },
  ];

  const filters = [
    ...(showTenant ? [{ key: "tenant_id", label: "Tenant", getValue: (r) => r.tenant_id, options: (tenantsData?.items || []).map((t) => ({ value: t.internalId, label: t.name })) }] : []),
    { key: "role", label: "Role", getValue: (r) => r.role, options: Object.entries(ROLE_LABELS)
      .filter(([v]) => showTenant || v !== "super_admin")
      .filter(([v]) => user?.role !== "manager"
        || (user?.permissions || []).includes("statistics.view.tenant") || v === "buyer")
      .map(([value, label]) => ({ value, label })) },
    { key: "manager_id", label: "Manager", getValue: (r) => r.manager_id || "", options: responsibles.map((m) => ({ value: m.id, label: m.role === "tenant_admin" ? `${m.name} (Admin)` : m.name })) },
    { key: "status", label: "Status", getValue: (r) => r.status, options: ["active", "hold", "disabled"].map((v) => ({ value: v, label: v })) },
  ];

  const impUsers = (user?.permissions || []).includes("impersonation.users.managed");
  const impManagers = (user?.permissions || []).includes("impersonation.managers.managed");
  const canImpersonate = ["super_admin", "tenant_admin"].includes(user?.role) || (user?.role === "manager" && (impUsers || impManagers));
  const impersonatable = (row) => {
    if (!canImpersonate || user?.is_impersonating || row.id === user?.id) return false;
    if (user?.role === "super_admin") return row.role !== "super_admin";
    if (user?.role === "tenant_admin") return ["manager", "buyer"].includes(row.role); // list is already tenant-scoped
    // manager: two independent permissions; admin accounts never impersonable
    return (row.role === "buyer" && impUsers) || (row.role === "manager" && impManagers);
  };
  const doImpersonate = async (row) => {
    try { await impersonateUser(row.id); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not log in as this user"); }
  };

  const changeManager = async (ids, clear, setBusy) => {
    if (!bulkMgr) return toast.error("Select the new manager first");
    setBusy(true);
    try {
      const { data: res } = await http.post("/users/bulk", { action: "set_manager", ids, manager_id: bulkMgr });
      qc.invalidateQueries({ queryKey: ["users"] });
      qc.invalidateQueries({ queryKey: ["options"] });
      toast.success(`Manager changed for ${res.affected} buyer${res.affected === 1 ? "" : "s"}`);
      setBulkMgr("");
      clear();
    } catch (e) {
      toast.error("Change manager failed", { description: e?.response?.data?.detail || "Try again." });
    } finally {
      setBusy(false);
    }
  };

  const rowActions = (row) => [
    { label: "Edit user", icon: Pencil, onClick: () => setDrawer({ open: true, item: row }), testId: `edit-user-${row.id}` },
    ...(impersonatable(row)
      ? [{ label: `Login as ${ROLE_LABELS[row.role] || "user"}`, icon: LogIn, onClick: () => doImpersonate(row), testId: `impersonate-user-${row.id}` }] : []),
    ...(hasPerm(user, "users.delete") ? [{ label: "Delete", icon: Trash2, danger: true, onClick: () => window.confirm("Delete user?") && remove.mutate(row.id), testId: `delete-user-${row.id}` }] : []),
  ];

  return (
    <div className="space-y-5">
      <PageHeader title="Users" subtitle="Managers and media buyers across your tenant." />
      <DataTablePro testId="users-table" columns={columns} rows={data?.items || []} loading={isLoading && !data}
        serverMode serverTotal={data?.total} onServerChange={handleServerChange} fetchAllRows={() => fetchAllPages("/users", qs)}
        filters={filters} searchKeys={["name", "email", "tenant_seq"]} searchPlaceholder="Search by ID, name or email..." exportName="users"
        onRowClick={(r) => setDrawer({ open: true, item: r })}
        rowActions={hasPerm(user, "users.edit") ? rowActions : null} emptyText="No users found."
        selectable={hasPerm(user, "users.edit") || hasPerm(user, "users.delete")} onBulkAction={bulk.handleBulk}
        bulkExtra={hasPerm(user, "users.edit") ? (({ ids, clear, busy, setBusy }) => (
          <div className="flex items-center gap-1.5" data-testid="users-bulk-manager">
            <SearchSelect value={bulkMgr} onChange={setBulkMgr} placeholder="New manager…"
              options={responsibles.map((m) => ({ value: m.id, label: m.role === "tenant_admin" ? `${m.name} (Admin)` : m.name }))}
              testId="bulk-manager-select" widthClass="w-[260px]" triggerClass="h-8 rounded-md text-xs" />
            <Button size="sm" variant="outline" disabled={busy || !bulkMgr} onClick={() => changeManager(ids, clear, setBusy)}
              data-testid="bulk-change-manager-btn" className="h-8 gap-1.5 border-white/[0.12] text-xs">
              <UserCog className="h-3.5 w-3.5" /> Change Manager
            </Button>
          </div>
        )) : undefined}
        toolbarRight={hasPerm(user, "users.create") && (
          <Button onClick={() => setDrawer({ open: true, item: null })} data-testid="add-user-btn" className="h-9 gap-1.5 bg-white text-black hover:bg-white/90"><Plus className="h-4 w-4" /> Add User</Button>
        )} />
      <UserDrawer open={drawer.open} item={drawer.item} onOpenChange={(o) => setDrawer((d) => ({ ...d, open: o }))} />
    </div>
  );
}
